import { useState, useRef, useEffect } from "react";
import { AIRPORTS } from "@/data/airports";
import { ChevronDown, Search, MapPin } from "lucide-react";

/**
 * Searchable airport dropdown.
 * value/onChange use the ICAO code (e.g. "KTEB").
 */
export default function AirportSelect({ value, onChange, placeholder = "Search airport…", testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = AIRPORTS.find(a => a.icao === value);
  const filtered = (q ? AIRPORTS.filter(a => {
    const s = q.toLowerCase();
    return a.icao.toLowerCase().includes(s) || a.iata.toLowerCase().includes(s) ||
           a.name.toLowerCase().includes(s) || a.city.toLowerCase().includes(s) ||
           a.country.toLowerCase().includes(s);
  }) : AIRPORTS).slice(0, 100);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full bg-white border border-[#E5DFD2] hover:border-[#A47E2A] focus:border-[#A47E2A] focus:outline-none px-4 py-2.5 rounded-sm text-[#1A1D24] text-sm text-left flex items-center justify-between transition-colors"
        data-testid={testId}
      >
        {selected ? (
          <span className="flex items-center gap-2 truncate">
            <span className="as-mono as-gold text-[11px]">{selected.iata}</span>
            <span className="truncate">{selected.city}, {selected.country}</span>
          </span>
        ) : <span className="text-[#756E64]">{placeholder}</span>}
        <ChevronDown size={14} className="text-[#756E64] shrink-0"/>
      </button>
      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-white border border-[#E5DFD2] rounded-sm shadow-lg overflow-hidden" data-testid={`${testId}-dropdown`}>
          <div className="p-2 border-b border-[#E5DFD2] flex items-center gap-2">
            <Search size={12} className="text-[#756E64]"/>
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by city, code, country…"
              className="flex-1 bg-transparent focus:outline-none text-sm text-[#1A1D24]"
              data-testid={`${testId}-search`}
            />
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <li className="p-4 text-center text-sm text-[#756E64]">No matches.</li>}
            {filtered.map(a => (
              <li
                key={a.icao}
                onClick={() => { onChange(a.icao); setOpen(false); setQ(""); }}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-3 hover:bg-[#F1EDE4] ${value === a.icao ? "bg-[#F1EDE4]" : ""}`}
                data-testid={`${testId}-option-${a.icao}`}
              >
                <MapPin size={11} className="as-gold shrink-0"/>
                <span className="as-mono text-[11px] w-14 shrink-0">{a.iata}</span>
                <span className="flex-1 truncate">{a.city}, <span className="text-[#756E64]">{a.country}</span></span>
                <span className="as-mono text-[10px] text-[#756E64]">{a.icao}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
